import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Box, Chip } from '@mui/material';
import { SelfServiceCampaignResponse } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryCampaignProvider/apis';

import { getPromotionState } from './utils';
import { UnreachableCode } from '@provider-portal/common/types/types';

import { $tk } from 'utils/appTranslationKey';

interface PromotionDetailsTitleProps {
  promotion: SelfServiceCampaignResponse | null;
}

const styles = {
  chipUpcoming: {
    color: '#5B68F6',
    backgroundColor: '#EBECFF',
  },
  chipEnded: {
    color: '#A9B0B9',
    backgroundColor: '#F4F5F6',
  },
} as const;

function getPromotionStateLabel(promotionState: 'ended' | 'scheduled' | 'active') {
  switch (promotionState) {
    case 'ended':
      return $tk.components.PromotionDetails.state.ended;
    case 'scheduled':
      return $tk.components.PromotionDetails.state.upcoming;
    case 'active':
      return $tk.components.PromotionDetails.state.active;
    default:
      return UnreachableCode.never(
        promotionState,
        `components.PromotionDetails.state.${promotionState}`,
      );
  }
}

export const PromotionDetailsTitle = ({ promotion }: PromotionDetailsTitleProps) => {
  const intl = useIntl();

  const state = useMemo(() => getPromotionState(promotion), [promotion]);
  const { title, chipLabel } = useMemo(() => {
    return {
      title: intl.formatMessage({ id: $tk.components.PromotionDetails.title }),
      chipLabel: intl.formatMessage({ id: getPromotionStateLabel(state) }),
    };
  }, [intl, state]);

  return (
    <Box component="span" display="inline-flex" alignItems="center">
      <Box component="span" marginRight={1}>
        {title}
      </Box>

      {state === 'scheduled' ? (
        <Chip size="small" label={chipLabel} sx={styles.chipUpcoming} />
      ) : null}
      {state === 'ended' ? <Chip size="small" label={chipLabel} sx={styles.chipEnded} /> : null}
    </Box>
  );
};
